import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
  Card,
  CardContent,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Devices as DevicesIcon
} from '@mui/icons-material';
import api from '../../utils/axios';

const AdminVenues = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [updateLoading, setUpdateLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Add device management states
  const [deviceDialogOpen, setDeviceDialogOpen] = useState(false);
  const [venueDevices, setVenueDevices] = useState([]);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [deviceError, setDeviceError] = useState('');
  const [newDevice, setNewDevice] = useState({
    name: '',
    ipAddress: '',
    apiKey: '',
    model: 'Exstreamer M400',
    notes: '',
    macAddress: ''
  });
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceEditDialogOpen, setDeviceEditDialogOpen] = useState(false);

  const fetchVenues = async () => {
    try {
      setLoading(true);
      setError('');
      setRefreshing(true);
      // Add debug information about authentication
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      console.log('Authentication state:', {
        hasToken: !!token,
        tokenLength: token ? token.length : 0,
        user: user,
        isAdmin: user?.isAdmin
      });
      
      console.log('Fetching venues...');
      const response = await api.get('/admin/venues');
      console.log('Venues API response:', response.data);
      
      // Process the data to add hasActiveSubscription property based on subscription_status
      const processedData = response.data.map(venue => ({
        ...venue,
        hasActiveSubscription: venue.subscription_status === 'active'
      }));
      
      setVenues(processedData);
    } catch (err) {
      console.error('Error fetching venues:', err);
      console.error('Error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        error: err.response?.data?.error,
        message: err.message
      });
      setError(err.response?.data?.error || 'Failed to fetch venues.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchVenues();
  }, []);

  const fetchVenueDevices = async (venueId) => {
    try {
      setDeviceLoading(true);
      setDeviceError('');
      const response = await api.get(`/admin/venues/${venueId}/devices`);
      setVenueDevices(response.data);
    } catch (err) {
      setDeviceError(err.response?.data?.error || 'Failed to fetch venue devices.');
    } finally {
      setDeviceLoading(false);
    }
  };

  const handleViewDetails = (venue) => {
    setSelectedVenue({
      ...venue,
      hasActiveSubscription: venue.subscription_status === 'active'
    });
    setOpenDetailsDialog(true);
    fetchVenueDevices(venue._id);
  };

  const handleCloseDetailsDialog = () => {
    setOpenDetailsDialog(false);
    setSelectedVenue(null);
  };

  const handleEditVenue = (venue) => {
    setSelectedVenue(venue);
    setEditFormData({
      name: venue.name,
      email: venue.email,
      'venueDetails.phone': venue.venueDetails?.phone || '',
      'venueDetails.website': venue.venueDetails?.website || '',
      'venueDetails.address.street': venue.venueDetails?.address?.street || '',
      'venueDetails.address.city': venue.venueDetails?.address?.city || '',
      'venueDetails.address.state': venue.venueDetails?.address?.state || '',
      'venueDetails.address.zip': venue.venueDetails?.address?.zip || '',
      'venueDetails.address.country': venue.venueDetails?.address?.country || '',
      'venueDetails.venueType': venue.venueDetails?.venueType || '',
      'venueDetails.capacity': venue.venueDetails?.capacity || '',
    });
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditFormData({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateVenue = async () => {
    if (!selectedVenue) return;
    
    try {
      setUpdateLoading(true);
      setError('');
      
      // Convert the flat form data back to a nested object
      const updateData = {
        name: editFormData.name,
        email: editFormData.email,
        venueDetails: {
          phone: editFormData['venueDetails.phone'],
          website: editFormData['venueDetails.website'],
          venueType: editFormData['venueDetails.venueType'],
          capacity: editFormData['venueDetails.capacity'] ? 
            parseInt(editFormData['venueDetails.capacity']) : undefined,
          address: {
            street: editFormData['venueDetails.address.street'],
            city: editFormData['venueDetails.address.city'],
            state: editFormData['venueDetails.address.state'],
            zip: editFormData['venueDetails.address.zip'],
            country: editFormData['venueDetails.address.country'],
          }
        }
      };
      
      await api.put(`/admin/venues/${selectedVenue._id}`, updateData);
      handleCloseEditDialog();
      await fetchVenues(); // Refresh venues list
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update venue.');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleToggleSubscription = async (venueId) => {
    try {
      setError('');
      await api.post(`/admin/venues/${venueId}/toggle-subscription`);
      await fetchVenues(); // Refresh venues list
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to toggle subscription status.');
    }
  };

  const handleOpenDeviceDialog = () => {
    setDeviceDialogOpen(true);
  };

  const handleCloseDeviceDialog = () => {
    setDeviceDialogOpen(false);
    setNewDevice({
      name: '',
      ipAddress: '',
      apiKey: '',
      model: 'Exstreamer M400',
      notes: '',
      macAddress: ''
    });
  };

  const handleDeviceInputChange = (e) => {
    const { name, value } = e.target;
    setNewDevice(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddDevice = async () => {
    try {
      setDeviceLoading(true);
      setDeviceError('');
      
      // Add the device for the selected venue
      await api.post(`/admin/venues/${selectedVenue._id}/devices`, newDevice);
      
      // Refresh devices list
      fetchVenueDevices(selectedVenue._id);
      handleCloseDeviceDialog();
    } catch (err) {
      setDeviceError(err.response?.data?.error || 'Failed to add device.');
    } finally {
      setDeviceLoading(false);
    }
  };

  const handleEditDevice = (device) => {
    setSelectedDevice(device);
    setDeviceEditDialogOpen(true);
  };

  const handleCloseDeviceEditDialog = () => {
    setDeviceEditDialogOpen(false);
    setSelectedDevice(null);
  };

  const handleUpdateDevice = async () => {
    try {
      setDeviceLoading(true);
      setDeviceError('');
      
      await api.put(
        `/admin/venues/${selectedVenue._id}/devices/${selectedDevice._id}`, 
        selectedDevice
      );
      
      // Refresh devices list
      fetchVenueDevices(selectedVenue._id);
      handleCloseDeviceEditDialog();
    } catch (err) {
      setDeviceError(err.response?.data?.error || 'Failed to update device.');
    } finally {
      setDeviceLoading(false);
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    try {
      setDeviceLoading(true);
      setDeviceError('');
      
      await api.delete(`/admin/venues/${selectedVenue._id}/devices/${deviceId}`);
      
      // Refresh devices list
      fetchVenueDevices(selectedVenue._id);
    } catch (err) {
      setDeviceError(err.response?.data?.error || 'Failed to delete device.');
    } finally {
      setDeviceLoading(false);
    }
  };

  const renderDetailsDialog = () => {
    if (!selectedVenue) return null;
    
    return (
      <Dialog 
        open={openDetailsDialog} 
        onClose={handleCloseDetailsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5">{selectedVenue.name}</Typography>
          <Typography variant="subtitle2" color="text.secondary">
            {selectedVenue.email} 
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Venue Details</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>Type:</strong> {selectedVenue.venueDetails?.venueType || 'Not specified'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>Capacity:</strong> {selectedVenue.venueDetails?.capacity || 'Not specified'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>Phone:</strong> {selectedVenue.venueDetails?.phone || 'Not specified'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>Website:</strong> {selectedVenue.venueDetails?.website || 'Not specified'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>Address:</strong> {
                          selectedVenue.venueDetails?.address?.street 
                            ? `${selectedVenue.venueDetails?.address?.street}, 
                               ${selectedVenue.venueDetails?.address?.city || ''} 
                               ${selectedVenue.venueDetails?.address?.state || ''} 
                               ${selectedVenue.venueDetails?.address?.zip || ''}, 
                               ${selectedVenue.venueDetails?.address?.country || ''}`
                            : 'Not specified'
                        }
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Subscription</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>Status:</strong>{' '}
                        <Chip 
                          size="small"
                          label={selectedVenue.hasActiveSubscription ? 'Active' : 'Inactive'} 
                          color={selectedVenue.hasActiveSubscription ? 'success' : 'error'}
                        />
                      </Typography>
                    </Grid>
                    {selectedVenue.subscription && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <strong>Plan:</strong> {selectedVenue.subscription.planName}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <strong>Start Date:</strong> {
                              new Date(selectedVenue.subscription.createdAt).toLocaleDateString()
                            }
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <strong>Billing Period:</strong> {selectedVenue.subscription.billingPeriod}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Devices ({selectedVenue.deviceCount || 0})
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  {selectedVenue.devices && selectedVenue.devices.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>IP Address</TableCell>
                            <TableCell>MAC Address</TableCell>
                            <TableCell>Model</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedVenue.devices.map((device) => (
                            <TableRow key={device._id}>
                              <TableCell>{device.name}</TableCell>
                              <TableCell>{device.ipAddress}</TableCell>
                              <TableCell>{device.macAddress || '-'}</TableCell>
                              <TableCell>{device.model}</TableCell>
                              <TableCell>
                                <Chip 
                                  size="small"
                                  label={device.isActive ? 'Active' : 'Inactive'} 
                                  color={device.isActive ? 'success' : 'error'}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2">No devices registered</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Barix Devices Section */}
            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DevicesIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">
                        Barix Audio Devices
                      </Typography>
                    </Box>
                    <Button 
                      variant="contained" 
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={handleOpenDeviceDialog}
                    >
                      Add Device
                    </Button>
                  </Box>
                  
                  {deviceError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {deviceError}
                    </Alert>
                  )}
                  
                  {deviceLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : venueDevices.length === 0 ? (
                    <Typography variant="body1" sx={{ textAlign: 'center', py: 3 }}>
                      No Barix devices registered for this venue.
                    </Typography>
                  ) : (
                    <List>
                      {venueDevices.map((device) => (
                        <ListItem
                          key={device._id}
                          divider
                          secondaryAction={
                            <Box>
                              <IconButton 
                                edge="end" 
                                aria-label="edit"
                                onClick={() => handleEditDevice(device)}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton 
                                edge="end" 
                                aria-label="delete"
                                onClick={() => handleDeleteDevice(device._id)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          }
                        >
                          <ListItemText
                            primary={device.name}
                            secondary={
                              <>
                                <Typography component="span" variant="body2">
                                  IP: {device.ipAddress} • Model: {device.model}
                                </Typography>
                                <br />
                                <Typography component="span" variant="body2">
                                  Status: {device.lastStatus?.playbackStatus || 'Unknown'}
                                </Typography>
                              </>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailsDialog}>Close</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => handleEditVenue(selectedVenue)}
          >
            Edit Details
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderEditDialog = () => {
    return (
      <Dialog 
        open={openEditDialog} 
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Venue: {selectedVenue?.name}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                margin="normal"
                label="Venue Name"
                name="name"
                value={editFormData.name || ''}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                margin="normal"
                label="Email"
                name="email"
                value={editFormData.email || ''}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                margin="normal"
                label="Phone"
                name="venueDetails.phone"
                value={editFormData['venueDetails.phone'] || ''}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                margin="normal"
                label="Website"
                name="venueDetails.website"
                value={editFormData['venueDetails.website'] || ''}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                margin="normal"
                label="Venue Type"
                name="venueDetails.venueType"
                value={editFormData['venueDetails.venueType'] || ''}
                onChange={handleInputChange}
                select
                SelectProps={{ native: true }}
              >
                <option value="">Select a venue type</option>
                <option value="Nightclub">Nightclub</option>
                <option value="Bar">Bar</option>
                <option value="Lounge">Lounge</option>
                <option value="Event Space">Event Space</option>
                <option value="Restaurant">Restaurant</option>
                <option value="Other">Other</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                margin="normal"
                label="Capacity"
                name="venueDetails.capacity"
                type="number"
                value={editFormData['venueDetails.capacity'] || ''}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Address
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                margin="normal"
                label="Street"
                name="venueDetails.address.street"
                value={editFormData['venueDetails.address.street'] || ''}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                margin="normal"
                label="City"
                name="venueDetails.address.city"
                value={editFormData['venueDetails.address.city'] || ''}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                margin="normal"
                label="State/Province"
                name="venueDetails.address.state"
                value={editFormData['venueDetails.address.state'] || ''}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                margin="normal"
                label="ZIP/Postal Code"
                name="venueDetails.address.zip"
                value={editFormData['venueDetails.address.zip'] || ''}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                margin="normal"
                label="Country"
                name="venueDetails.address.country"
                value={editFormData['venueDetails.address.country'] || ''}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button 
            variant="contained"
            onClick={handleUpdateVenue}
            disabled={updateLoading}
          >
            {updateLoading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderDeviceDialog = () => {
    return (
      <Dialog
        open={deviceDialogOpen}
        onClose={handleCloseDeviceDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Barix Device for {selectedVenue?.name}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Device Name"
            name="name"
            value={newDevice.name}
            onChange={handleDeviceInputChange}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            label="IP Address"
            name="ipAddress"
            value={newDevice.ipAddress}
            onChange={handleDeviceInputChange}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            label="API Key"
            name="apiKey"
            value={newDevice.apiKey}
            onChange={handleDeviceInputChange}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            label="MAC Address"
            name="macAddress"
            value={newDevice.macAddress}
            onChange={handleDeviceInputChange}
            placeholder="00:08:E1:0B:BC:80"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Device Model</InputLabel>
            <Select
              name="model"
              value={newDevice.model}
              onChange={handleDeviceInputChange}
              label="Device Model"
            >
              <MenuItem value="Exstreamer M400">Exstreamer M400</MenuItem>
              <MenuItem value="RetailPlayer M400">RetailPlayer M400</MenuItem>
              <MenuItem value="MPA400">MPA400</MenuItem>
              <MenuItem value="IP Former">IP Former</MenuItem>
              <MenuItem value="Other">Other Barix Device</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            label="Notes"
            name="notes"
            value={newDevice.notes}
            onChange={handleDeviceInputChange}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeviceDialog}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleAddDevice}
            disabled={!newDevice.name || !newDevice.ipAddress || !newDevice.apiKey}
          >
            Add Device
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderDeviceEditDialog = () => {
    if (!selectedDevice) return null;
    
    return (
      <Dialog
        open={deviceEditDialogOpen}
        onClose={handleCloseDeviceEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Barix Device</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Device Name"
            name="name"
            value={selectedDevice.name}
            onChange={(e) => setSelectedDevice({...selectedDevice, name: e.target.value})}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            label="IP Address"
            name="ipAddress"
            value={selectedDevice.ipAddress}
            onChange={(e) => setSelectedDevice({...selectedDevice, ipAddress: e.target.value})}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            label="API Key"
            name="apiKey"
            value={selectedDevice.apiKey}
            onChange={(e) => setSelectedDevice({...selectedDevice, apiKey: e.target.value})}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            label="MAC Address"
            name="macAddress"
            value={selectedDevice.macAddress || ''}
            onChange={(e) => setSelectedDevice({...selectedDevice, macAddress: e.target.value})}
            placeholder="00:08:E1:0B:BC:80"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Device Model</InputLabel>
            <Select
              name="model"
              value={selectedDevice.model}
              onChange={(e) => setSelectedDevice({...selectedDevice, model: e.target.value})}
              label="Device Model"
            >
              <MenuItem value="Exstreamer M400">Exstreamer M400</MenuItem>
              <MenuItem value="RetailPlayer M400">RetailPlayer M400</MenuItem>
              <MenuItem value="MPA400">MPA400</MenuItem>
              <MenuItem value="IP Former">IP Former</MenuItem>
              <MenuItem value="Other">Other Barix Device</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            label="Notes"
            name="notes"
            value={selectedDevice.notes || ''}
            onChange={(e) => setSelectedDevice({...selectedDevice, notes: e.target.value})}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeviceEditDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              handleDeleteDevice(selectedDevice._id);
              handleCloseDeviceEditDialog();
            }}
          >
            Delete
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateDevice}
            disabled={!selectedDevice.name || !selectedDevice.ipAddress || !selectedDevice.apiKey}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Admin: Manage Venues
          </Typography>
          <IconButton 
            onClick={fetchVenues} 
            disabled={refreshing}
            color="primary"
            aria-label="refresh"
          >
            {refreshing ? <CircularProgress size={24} /> : <RefreshIcon />}
          </IconButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper elevation={3}>
            <TableContainer>
              <Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    {!isMobile && <TableCell>Email</TableCell>}
                    {!isMobile && <TableCell>Type</TableCell>}
                    <TableCell>Contact</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Subscription</TableCell>
                    <TableCell>Devices</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {venues.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 5 : 7} align="center">
                        No venues found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    venues.map((venue) => (
                      <TableRow key={venue._id}>
                        <TableCell>{venue.name}</TableCell>
                        {!isMobile && <TableCell>{venue.email}</TableCell>}
                        {!isMobile && (
                          <TableCell>{venue.venueDetails?.venueType || '-'}</TableCell>
                        )}
                        <TableCell>
                          {venue.venueDetails?.contactPerson || '-'}
                        </TableCell>
                        <TableCell>
                          {venue.venueDetails?.address?.city 
                            ? `${venue.venueDetails.address.city}, ${venue.venueDetails.address.country || '-'}` 
                            : '-'
                          }
                        </TableCell>
                        <TableCell>
                          <Chip 
                            size="small"
                            label={venue.hasActiveSubscription ? 'Active' : 'Inactive'} 
                            color={venue.hasActiveSubscription ? 'success' : 'error'}
                          />
                        </TableCell>
                        <TableCell>
                          {venue.deviceCount || 0}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Tooltip title="View Details">
                              <IconButton 
                                size="small" 
                                color="info" 
                                onClick={() => handleViewDetails(venue)}
                              >
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={() => handleEditVenue(venue)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={venue.hasActiveSubscription ? "Deactivate Subscription" : "Activate Subscription"}>
                              <IconButton 
                                size="small" 
                                color={venue.hasActiveSubscription ? "error" : "success"}
                                onClick={() => handleToggleSubscription(venue._id)}
                              >
                                {venue.hasActiveSubscription ? <CloseIcon fontSize="small" /> : <CheckIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>

      {renderDetailsDialog()}
      {renderEditDialog()}
      {renderDeviceDialog()}
      {renderDeviceEditDialog()}
    </Container>
  );
};

export default AdminVenues; 